import { useAppSelector } from 'hooks/redux';
import React, { useMemo, useState } from 'react';
import { makeStyles } from 'tss-react/mui';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { type goalsTypes, goalsSelectors } from 'app-components/flux/goals';
import { scheduledTransactionsSelectors } from 'app-components/flux/scheduled-transactions';
import type { tagsTypes } from 'app-components/flux/tags';
import { transactionsSelectors } from 'app-components/flux/transactions';

import StdDialog from 'components/Dialogs/StdDialog';
import StdDialogActions from 'components/Dialogs/StdDialogActions';
import StdDialogContent from 'components/Dialogs/StdDialogContent';
import LoadAllTransactionsHelper from 'containers/Settings/SharedComponents/LoadAllTransactionsHelper';

import { removeTxnTag, txnHasTag } from 'data/transactions/utils';

type Props = {
  tag: tagsTypes.Tag;
  onClose: any;
  deleteItem: any;
};

const useStyles = makeStyles()((theme) => ({
  txnStatus: {
    color: theme.palette.text.secondary,
    marginTop: 24,
  },
}));

const DeleteTagDialog = (props: Props) => {
  const { tag, onClose, deleteItem } = props;
  const { classes } = useStyles();

  const scheduledTransactions = useAppSelector(scheduledTransactionsSelectors.getUserVerifiedScheduledTransactions);
  const transactionsById = useAppSelector(transactionsSelectors.getTransactionsById);
  const goalsById = useAppSelector(goalsSelectors.getGoalsById);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const txnsToChange = useMemo(
    () =>
      transactionsById
        ?.filter((txn) => txnHasTag(txn, tag))
        .map((txn) => removeTxnTag(txn, tag))
        .toList() || {},
    [tag, transactionsById],
  );

  const scheduledTxnsToChange = useMemo(
    () =>
      scheduledTransactions
        ?.filter((sTxn) => txnHasTag(sTxn.transaction, tag))
        .map((sTxn) => removeTxnTag(sTxn.transaction, tag))
        .toList() || {},
    [scheduledTransactions, tag],
  );

  const txnText = `${txnsToChange.size} transaction${txnsToChange.size === 1 ? '' : 's'}`;
  const scheduledTxnText = scheduledTxnsToChange.size > 0 ? ` and ${scheduledTxnsToChange.size} recurring series` : '';
  const txnStatusTxt = `This tag is assigned to ${txnText}${scheduledTxnText}.`;

  let isGoal = false;
  if (tag.type === 'GOAL') {
    // verify goal is active
    const foundGoal = goalsById.find((goal: goalsTypes.Goal) => goal.tagId === tag.id);
    if (foundGoal && !foundGoal.isDeleted) isGoal = true;
  }

  const handleSubmit = () => {
    setIsSubmitting(true);
    requestAnimationFrame(() => {
      deleteItem(tag.set('isDeleted', true), txnsToChange, scheduledTxnsToChange);
    });
  };

  return (
    <StdDialog disableBackdropClick onClose={onClose} open showCloseButton size='sm' title='Delete Tag'>
      <StdDialogContent
        id='delete_tag_dialog_content'
        sx={{
          display: 'flex',
          flex: 1,
          flexDirection: 'column',
        }}
      >
        {isGoal ? (
          <Typography>Oops, This Tag is part of an active goal and it cannot be deleted.</Typography>
        ) : (
          <Box display='flex' flexDirection='column'>
            <Typography>Are you sure you want to delete the tag?</Typography>
            <Typography variant='captionRegular' className={classes.txnStatus}>
              {txnStatusTxt}
            </Typography>
            <Box marginTop={3}>
              <LoadAllTransactionsHelper
                tooltipText='This will temporarily load all transactions in the app to search and remove the tag.'
                initiator='delete-tag'
              />
            </Box>
          </Box>
        )}
      </StdDialogContent>
      <StdDialogActions
        primary={{
          color: isGoal ? 'primary' : 'error',
          disabled: isSubmitting,
          children: isGoal ? 'Got it' : 'Delete',
          variant: 'outlined',
          onClick: isGoal ? onClose : handleSubmit,
        }}
        secondary={
          isGoal
            ? undefined
            : {
                color: 'primary',
                variant: 'outlined',
                onClick: onClose,
              }
        }
      />
    </StdDialog>
  );
};

export default DeleteTagDialog;
